import { Box, Heading, useToast, Icon, HStack } from '@chakra-ui/react';
import { RiAddLine } from 'react-icons/ri';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { LinkButton } from '../../../../../components/LinkButton';
import {
  maskCpf,
  maskShortDate,
} from '../../../../../utils/formatters/handleMask';
import { IMember, MemberTable } from './components/MemberTable';
import { listUsersService } from '../../../../../services/Users/ListUsersService';
import { translateError } from '../../../../../utils/errors';
import { useAuth } from '../../../../../hooks/auth';

interface ILoadMembersProps {
  cpf?: string;
  email?: string;
  name?: string;
  phone?: string;
  ventureId?: string;
  sort?: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const MemberList = (): JSX.Element => {
  const toast = useToast();

  const { user: authenticatedUser } = useAuth();

  const [membersList, setMembersList] = useState<IMember[]>([]);
  const [currentFilters, setCurrentFilters] =
    useState<{ [key: string]: string }>();
  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>('ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('name');
  const [sortBy, setSortBy] = useState<
    'cpf' | 'name' | 'phone' | 'email' | 'createdAt'
  >('createdAt');
  const [totalPages, setTotalPages] = useState<number>();

  const loadMembers = useCallback(
    async ({
      cpf,
      email,
      name,
      phone,
      page,
      sort,
      order,
    }: ILoadMembersProps) => {
      try {
        const members = await listUsersService({
          cpf: cpf || undefined,
          email: email || undefined,
          name: name || undefined,
          phone: phone || undefined,
          featureGroups: ['MEMBER', 'DEPENDANT'],
          limit: 20,
          page,
          sort,
          order,
        });

        const parsedMembers = members.items.map((member) => ({
          ...member,
          cpf: member.cpf ? maskCpf(member.cpf) : undefined,
          formattedDate: maskShortDate(member.createdAt),
        }));

        setTotalPages(members.pages);
        setMembersList(parsedMembers);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Failed to load data',
            description:
              translateError({ message: err.response?.data.message }) ||
              'An error occurred while loading member data, please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    loadMembers({
      ...currentFilters,
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [currentFilters, currentPage, sortBy, loadMembers, currentOrdering]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters({ [filterBy]: value });
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleSortList = useCallback(
    (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => {
      setSortBy(sort);
    },
    [],
  );

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <HStack justify="space-between">
          <Heading size="lg" fontWeight="normal">
            Members
          </Heading>

          {authenticatedUser.featureGroup.features.some((feature) =>
            ['USER_FULL_ACCESS', 'USER_WRITE_ACCESS'].includes(feature.key),
          ) && (
            <LinkButton
              colorScheme="green"
              icon={<Icon as={RiAddLine} fontSize="16" />}
              to="/members/register"
            >
              Register new
            </LinkButton>
          )}
        </HStack>

        <MemberTable
          mt="4"
          members={membersList}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
        />
      </Box>
    </DefaultLayout>
  );
};
